/**
 * Combine strings in array L2R that can fit jointly under a max length
 */
export function compactStringArray(strings: string[], maxLength: number, delim = ''): string[] {
  return strings.reduce((acc, str) => {
    // can this value be added to the prior without going over maxLength
    if (acc.length > 0 && acc[acc.length - 1].length + delim.length + str.length <= maxLength) {
      acc[acc.length - 1] = acc[acc.length - 1] + delim + str;
    } else {
      acc.push(str);
    }
    return acc;
  }, []);
}

/**
 * Split text string in into array of strings all under a max length
 *
 * Tries to wrap the text in a user friendly way.
 */
export function wrapText(text: string, maxLength: number): string[] {
  if (text.length <= maxLength) {
    return [text];
  }

  // Try to split on spaces
  let splitLines = text.split(' ');
  if (splitLines.findIndex(line => line.length > maxLength) === -1) {
    return compactStringArray(splitLines, maxLength, ' ');
  }

  // Try to split on "/" first which is nicer for paths than just length
  splitLines = text.match(/[^/]+\/?|\//g);
  if (splitLines.findIndex(line => line.length > maxLength) === -1) {
    return compactStringArray(splitLines, maxLength);
  }

  // Otherwise split by length disregarding slashes or spaces
  return text.match(new RegExp(`.{1,${maxLength}}`, 'g'));
}

/**
 * Break up each line of text as wrapped by wrapText into [tag, string] pairs
 *
 * wrappedTextLines - Array of strings as returned by wrapText()
 * originalText - Text string before being wrapped
 * tagBlocks - Array of { start, end } objects denoting a substring in
 *   originalText to tag. Must not overlap and be in the order they appear.
 * tag - Something denote string segment was from a match in tagBlocks
 *
 * Returns array like wrappedTextLines but with each string line replaced
 *   with an array of [TAG, STRING] pairs, where the STRING's start and stop
 *   is based on substrings from tagBlocks being found and TAG is either the
 *   tag argument or null which denotes unmatched text.
 */
export function segmentLines(
  wrappedTextLines: string[],
  originalText: string,
  tagBlocks: { start: number, end: number }[],
  tag: string,
  untagged = null,
): string[][][] {
  let scanIndex = 0;
  const segmentedLines: string[][][] = [];
  wrappedTextLines.forEach((line, i) => {
    const lineEnd: number = line.length - 1;
    let segments: string[][] = [];

    // Find any tagged blocks appearing on this line
    // Note, block may have been started on preceding line
    const applicable = tagBlocks.filter(block => {
      const start = block.start - scanIndex;
      const end = block.end - scanIndex;
      return (start > -1 && start <= lineEnd) || (end > -1 && end <= lineEnd);
    });

    let currentTag = untagged;
    let segmentStart = 0;
    let segmentEnd = lineEnd;

    // Entire line is untagged
    if (applicable.length === 0) {
      segments = [[untagged, line]];
    }

    // Work through blocks opening and closing segments upon block start and end
    applicable.forEach(block => {
      const start = block.start - scanIndex;
      const end = block.end - scanIndex;

      // Capture any unmatched text before the start of this block into a segment
      if (start > segmentStart) {
        currentTag = untagged;
        segmentEnd = start - 1;
        segments.push([currentTag, line.substring(segmentStart, segmentEnd + 1)]);
        segmentStart = segmentEnd + 1;
      }

      // The matched segment up to either it's end or the line end, whichever is first
      currentTag = tag;
      segmentEnd = Math.min(end, lineEnd);
      segments.push([currentTag, line.substring(segmentStart, segmentEnd + 1)]);

      segmentStart = segmentEnd + 1;
    });

    // Capture any unmatched text at the end of this line
    if (segmentEnd < lineEnd) {
      currentTag = untagged;
      segmentEnd = lineEnd;
      segments.push([currentTag, line.substring(segmentStart, segmentEnd + 1)]);
    }

    segmentedLines.push(segments);

    scanIndex += line.length;
    // Skip over any space character that was replaced with newline
    if (i + 1 < wrappedTextLines.length && wrappedTextLines[i + 1][0] !== originalText[scanIndex]) {
      scanIndex += 1;
    }
  });
  return segmentedLines;
}
